<template>
  <section class="resend">
    <translate tag="h1"> Resend confirmation email </translate>
    <state-matches v-if="state" :state="state">
      <template #final>
        <translate tag="p">
          We have re-sent your latest confirmation email. Don't see it? Please
          check your spam folder.
        </translate>
      </template>
      <template #error>
        <p>Cannot resend your latest confirmation email. An error occurred.</p>
      </template>
      <template #idle>
        <translate tag="p">
          Enter your email address and we'll resend the last confirmation we
          sent to you.
        </translate>
        <br />
        <Form @submit="submit()">
          <base-input
            v-model="email"
            autocapitalize="off"
            autocomplete="email"
            autocorrect="off"
            name="email"
            type="email"
            spellcheck="false"
            :validation-rules="emailValidationRules"
            :required="true"
          >
            <template #label>
              <translate>Email address</translate>
            </template>
            <template #errorMessage="{ failedRules }">
              <translate v-if="failedRules === 'exists'" tag="span">
                This email address is already associated with an account
              </translate>
              <translate v-if="failedRules === 'required'">
                Please enter your email address
              </translate>
              <translate v-if="failedRules === 'email'">
                Please enter a valid email address
              </translate>
            </template>
          </base-input>
          <div class="button-wrapper">
            <base-submit-button class="secondary" />
          </div>
        </Form>
      </template>
      <template #submitting>
        <translate>Processing…</translate>
      </template>
    </state-matches>
    <nuxt-to-meta :nuxt2meta="head" />
  </section>
</template>

<script lang="ts">
import { Form } from 'vee-validate'
import * as z from 'zod'
import { Component, Vue, toNative } from '~/utility/pu-class-decorator'
import { getResendLastBookingConfirmationFactory } from './inversify.config'
import {
  MachineContext,
  MachineStateType,
  ResendLastBookingConfirmationService,
} from './machine'

@Component({
  components: { Form },
})
class ResendConfirmationEmailMain extends Vue {
  email = ''

  serviceFactory = getResendLastBookingConfirmationFactory()

  service: ResendLastBookingConfirmationService | null = null
  context: MachineContext | null = null
  state: MachineStateType | null = null

  get head() {
    return {
      title: (this.$t('Resend confirmation email') +
        ' | Pitchup.com') as string,
      meta: [
        {
          hid: 'robots',
          name: 'robots',
          content: 'noindex',
        },
        {
          hid: 'description',
          name: 'description',
          property: 'og:description',
          content:
            'Resend the confirmation email from your last booking on Pitchup.com to your email address.',
        },
      ],
    }
  }

  created() {
    // Check for email in query params
    const queryEmail = this.$route.query.email as string
    if (queryEmail) {
      this.email = queryEmail
    }

    const service = this.serviceFactory({
      email: this.email,
    }) as ResendLastBookingConfirmationService
    this.service = service
      .onTransition((state) => {
        this.state = state
        this.context = state.context
      })
      .start()
  }

  emailValidationRules = z
    .string({ required_error: 'required' })
    .min(1, { message: 'required' })
    .email({ message: 'email' })

  submit() {
    this.service?.send({ type: 'SUBMIT_FORM', email: this.email })
  }
}

export default toNative(ResendConfirmationEmailMain)
</script>
<style scoped>
section {
  padding: 1em;
}

p {
  font-size: var(--body);
}

h1 {
  font-size: 1.25em;
  color: var(--colour-dark-blue);
}

.resend {
  flex: 1 0 auto;
}

.resend + footer {
  flex-shrink: 0;
}

.button-wrapper {
  text-align: right;
}
</style>
